import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import type { IDeployNote, IModule } from "@multiconsult-group/multiplacer-core";
import { MULTIPLACER_MODULES } from "@multiconsult-group/multiplacer-core";
import { useGetMultiplacerReleaseNotesDeployNotesUnpublishedQuery } from "../../openapi";
import { DeployChangeItemMediaUpload } from "./releasenotesadmin/DeployChangeItemMediaUpload";

export type UnpublishedDeployNotesCardProps = {
  onSelectionChange?: (selectedDeployNoteIds: string[]) => void;
};

const MODULE_OPTIONS = [
  { value: "ALL", label: "Alle moduler" },
  ...MULTIPLACER_MODULES.map((m) => ({
    value: m.key,
    label: `${m.title} (${m.key})`,
  })),
] as const;

const getIdString = (v: unknown): string => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  const anyV: any = v;
  if (typeof anyV?.toHexString === "function") return anyV.toHexString();
  if (typeof anyV?.$oid === "string") return anyV.$oid;
  return "";
};

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("nb-NO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const UnpublishedDeployNotesCard: React.FC<UnpublishedDeployNotesCardProps> = ({
  onSelectionChange,
}) => {
  const [moduleFilter, setModuleFilter] = useState<IModule | "ALL">("ALL");

  const queryArg = useMemo(
    () => ({
      limit: 50,
      cursor: undefined as string | undefined,
      module: moduleFilter === "ALL" ? undefined : moduleFilter,
    }),
    [moduleFilter]
  );

  const { data, isLoading, isError, error, refetch } =
    useGetMultiplacerReleaseNotesDeployNotesUnpublishedQuery(queryArg);

  const items: IDeployNote[] = data?.items ?? [];

  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  const selectedList = useMemo(
    () =>
      Object.entries(selectedIds)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [selectedIds]
  );

  React.useEffect(() => {
    onSelectionChange?.(selectedList);
  }, [selectedList, onSelectionChange]);

  const toggle = (id: string, next: boolean) => {
    setSelectedIds((prev) => ({ ...prev, [id]: next }));
  };

  const selectAllVisible = () => {
    const next = { ...selectedIds };
    for (const dn of items) {
      const id = getIdString((dn as any)._id);
      if (id) next[id] = true;
    }
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds({});

  const errorMessage = isError
    ? (error as any)?.data?.message || "Det oppstod en feil under lasting."
    : undefined;

  return (
    <Card withBorder radius="lg" p="md">
      <Stack>
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <Title order={3}>Upubliserte deploy-notater</Title>
            <Badge variant="light">{items.length}</Badge>
          </Group>

          <Group gap="xs">
            <Select
              value={moduleFilter}
              onChange={(v) => setModuleFilter((v ?? "ALL") as any)}
              data={MODULE_OPTIONS as any}
              allowDeselect={false}
              w={260}
            />
            <Button variant="light" onClick={() => refetch()}>
              Oppdater
            </Button>
          </Group>
        </Group>

        <Text size="sm" c="dimmed">
          Velg deploy-notater som skal inngå i neste release. (Publisering kommer i neste steg.)
        </Text>

        {/* States */}
        {isLoading && (
          <Group>
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Laster…
            </Text>
          </Group>
        )}

        {errorMessage && (
          <Alert color="red" title="Kunne ikke laste" variant="light">
            {errorMessage}
          </Alert>
        )}

        {!isLoading && !errorMessage && items.length === 0 && (
          <Alert color="gray" title="Ingen upubliserte deploy-notater" variant="light">
            Alt er publisert. Lag et nytt deploy-notat først.
          </Alert>
        )}

        {/* List */}
        {!isLoading && items.length > 0 && (
          <>
            <Group justify="space-between">
              <Group gap="xs">
                <Button variant="light" onClick={selectAllVisible}>
                  Velg alle (synlige)
                </Button>
                <Button variant="subtle" onClick={clearSelection}>
                  Fjern valg
                </Button>
              </Group>

              <Text size="sm" c="dimmed">
                Valgt: {selectedList.length}
              </Text>
            </Group>

            <Stack gap="sm">
              {items.map((dn) => {
                const deployNoteId = getIdString((dn as any)._id);
                const checked = !!selectedIds[deployNoteId];

                const headline =
                  dn.changeItems?.find((x) => x.userFacing)?.text ??
                  dn.changeItems?.[0]?.text ??
                  "(Ingen tekst)";

                const module = dn.changeItems?.[0]?.module ?? "CEILING";
                const type = dn.changeItems?.[0]?.type ?? "changed";

                return (
                  <Card key={deployNoteId} withBorder radius="md" p="sm">
                    {/* Deploy note summary */}
                    <Group justify="space-between" align="flex-start">
                      <Group align="flex-start">
                        <Checkbox
                          checked={checked}
                          onChange={(e) => toggle(deployNoteId, e.currentTarget.checked)}
                          mt={4}
                        />

                        <Stack gap="xs">
                          <Group gap="xs">
                            <Badge variant="light">{module}</Badge>
                            <Badge variant="light">{type}</Badge>
                            <Text size="sm" c="dimmed">
                              {formatDateTime(dn.mergedAt)}
                            </Text>
                          </Group>

                          <Text>{headline}</Text>

                          {dn.internalNote && (
                            <Text size="sm" c="dimmed">
                              {dn.internalNote}
                            </Text>
                          )}
                        </Stack>
                      </Group>

                      <Text size="xs" c="dimmed">
                        {dn.gitSha ?? ""}
                      </Text>
                    </Group>

                    {/* Change items (details) */}
                    <Stack gap="sm" mt="sm">
                      {(dn.changeItems ?? []).map((ci, idx) => {
                        const changeItemId = getIdString((ci as any)._id);

                        return (
                          <Card key={changeItemId || idx} withBorder radius="md" p="sm">
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Group gap="xs">
                                  <Badge variant="light">Item #{idx + 1}</Badge>
                                  {ci.userFacing && <Badge variant="filled">Sluttbruker</Badge>}
                                </Group>

                                <Text size="xs" c="dimmed">
                                  {ci.area}
                                </Text>
                              </Group>

                              <Text size="sm">{ci.text}</Text>

                              <DeployChangeItemMediaUpload
                                deployNoteId={deployNoteId}
                                changeItemId={changeItemId}
                                existingMedia={ci.media}
                              />
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default UnpublishedDeployNotesCard;
